import { useState } from 'react';
import { FileText, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';

/**
 * Choose a password after following an invite or reset link.
 *
 * An invited account exists but has no password. Supabase's link signs the
 * person in via the URL fragment, so without this screen they land in the app
 * once and are then locked out permanently — the sign-in form asks for a
 * password they were never given a chance to set.
 */
export default function SetPassword({ mode = 'invite', linkError = null, email = '' }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const isInvite = mode === 'invite';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    // Supabase's own minimum is 6; 10 is a small ask for an account that can
    // buy postage, and the rule is stated up front rather than after a failure.
    if (password.length < 10) {
      setError('Use at least 10 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;

      // The session from the link is already live, so the app opens straight
      // away. Reload so the shell re-reads it cleanly.
      window.location.replace('/');
    } catch (err) {
      setError(err.message || 'Could not set the password.');
      setBusy(false);
    }
  };

  if (linkError) {
    return (
      <div className="signin-shell">
        <div className="signin-card">
          <div className="signin-brand">
            <FileText size={36} />
            <div>
              <h1>EDDM Organizer</h1>
              <p>USPS Mail Management</p>
            </div>
          </div>

          <div className="alert alert-error">{linkError}</div>

          <p className="signin-help">
            Invite and reset links expire. Ask an administrator to send a new one.
          </p>
          <button className="btn btn-outline full-width" onClick={() => window.location.replace('/')}>
            Back to sign in
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="signin-shell">
      <form className="signin-card" onSubmit={handleSubmit}>
        <div className="signin-brand">
          <FileText size={36} />
          <div>
            <h1>EDDM Organizer</h1>
            <p>{isInvite ? 'Choose a password to finish setting up' : 'Choose a new password'}</p>
          </div>
        </div>

        {email && (
          <p className="signin-help" style={{ textAlign: 'left', marginTop: 0, marginBottom: '1rem' }}>
            Signing in as <strong>{email}</strong>
          </p>
        )}

        {error && <div className="alert alert-error">{error}</div>}

        <div className="form-group">
          <label className="form-label" htmlFor="new-password">
            New password
          </label>
          <input
            id="new-password"
            className="form-input"
            type="password"
            autoComplete="new-password"
            autoFocus
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>

        <div className="form-group">
          <label className="form-label" htmlFor="confirm-password">
            Confirm password
          </label>
          <input
            id="confirm-password"
            className="form-input"
            type="password"
            autoComplete="new-password"
            required
            value={confirm}
            onChange={(event) => setConfirm(event.target.value)}
          />
        </div>

        <button className="btn btn-primary signin-submit" type="submit" disabled={busy}>
          {busy ? <Loader2 size={18} className="spin" /> : null}
          {busy ? 'Saving…' : 'Set password and continue'}
        </button>

        <p className="signin-help">At least 10 characters.</p>
      </form>
    </div>
  );
}
